import {
  memo,
  ReactElement,
  Suspense,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Popover } from 'antd';
import cc from 'classcat';
import { EContainerIconSize, EIconSize } from 'utils/constants/app/ui';
import { MILLISECONDS_IN_SECOND } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { getPopoverPlacement } from 'utils/sportsbook/match-statistic';
import { TPopoverPositions } from 'interfaces/match-statistic';
import { EventAction } from 'components/EventAction';
import { GlobalIcon } from 'components/GlobalIcon';
import { useMounted } from 'hooks/useMounted';
import { MatchStatisticPopoverSkeleton } from '../MatchStatisticPopover/Skeleton';
import './index.less';

const { MatchStatisticPopover } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-statistic-popover" */ '../MatchStatisticPopover'
    )
);

type TProps = {
  matchId: number;
  showStatisticInPopover?: boolean;
  size?: EIconSize._16 | EIconSize._20;
  iconContainerSize?:
    | EContainerIconSize.SMALL
    | EContainerIconSize.MIDDLE
    | EContainerIconSize.LARGE
    | 0;
  className?: string;
};

export const MatchStatistic = memo(
  ({
    matchId,
    showStatisticInPopover = true,
    size,
    iconContainerSize,
    className = ''
  }: TProps): ReactElement => {
    const [triggerId] = useState(
      `popover-trigger-${Math.floor(Math.random() * MILLISECONDS_IN_SECOND)}`
    );

    const [placement, setPLacement] = useState<TPopoverPositions | null>(null);

    const { mounted } = useMounted();

    useEffect(() => {
      if (showStatisticInPopover && !isMobile() && mounted.current) {
        const handleSetPlacement = () => {
          setPLacement(getPopoverPlacement(triggerId));
        };

        handleSetPlacement();

        window.addEventListener('scroll', handleSetPlacement);

        return () => {
          window.removeEventListener('scroll', handleSetPlacement);
        };
      }
    }, [mounted.current]);

    useEffect(() => {
      return () => {
        setPLacement(null);
      };
    }, []);

    const handleRedirect = useCallback(
      (event: SyntheticEvent<Element, Event>) => {
        event.stopPropagation();

        window.open(
          `${SpringConfigs.STATISTICS_URL}/#/${SpringConfigs.STATISTICS_LANG_PREFIX}/external/page/redirect/${matchId}`
        );
      },
      []
    );

    const icon = useMemo(() => {
      return (
        <EventAction
          className={className}
          iconContainerSize={iconContainerSize}
          icon={
            <GlobalIcon
              lib="generic"
              name="statistics"
              theme="default"
              className={cc([
                'match-statistic',
                { 'match-statistic-mobile': isMobile() }
              ])}
              onClick={handleRedirect}
              size={size}
              data-popover-trigger={triggerId}
            />
          }
        />
      );
    }, []);

    if (!showStatisticInPopover || isMobile() || !placement) {
      return icon;
    }

    return (
      <Popover
        destroyTooltipOnHide
        trigger="hover"
        overlayClassName="match-statistic-popover"
        placement={placement}
        content={
          <Suspense fallback={<MatchStatisticPopoverSkeleton />}>
            <MatchStatisticPopover matchId={matchId} />
          </Suspense>
        }
      >
        {icon}
      </Popover>
    );
  }
);
